import type { Squid } from "@0xsquid/sdk";
import {
  ChainData,
  ChainType,
  CosmosChain,
  EvmChain,
  Token,
} from "@0xsquid/squid-types";
import { QueryClient } from "@tanstack/react-query";
import BigNumber from "bignumber.js";
import {
  CHAIN_IDS,
  destinationAddressResetValue,
  fallbackAddressResetValue,
  nativeEvmTokenAddress,
} from "../../core/constants";
import { FullAddressData } from "../../core/types";
import type { AppConfig } from "../../core/types/config";
import { SwapDirection, SwapParams } from "../../core/types/swap";
import type { TokenGroup, TokenWithBalance } from "../../core/types/tokens";
import {
  filterTokensForDestination,
  getInitialOrDefaultTokenAddressForChain,
  getTokensForChain,
} from "./configService";
import { convertTokenAmountToUSD } from "./priceService";
import { isWalletAddressValid } from "./walletService";

export const assetsBaseUrl =
  "https://raw.githubusercontent.com/0xsquid/assets/main";

export const shareSubgraphId = (token1: Token, token2: Token) => {
  return Boolean(
    token1?.subGraphIds?.some((sgi) => !!token2?.subGraphIds?.includes(sgi)) ||
      token2?.subGraphIds?.some((sgi) => !!token1?.subGraphIds?.includes(sgi))
  );
};

/**
 * Check if `tokenToCheck` shares any subgraph id with the tokens being compared
 * If any of the tokens being compared shares a subgraph id with `tokenToCheck`, it will be sorted first
 * @param a first token being compared
 * @param b second token being compared
 * @param tokenToCheck the token to check subgraph ids against
 */
export const sortTokensBySharedSubgraphIds = ({
  a,
  b,
  tokenToCheck,
}: {
  a: Token;
  b: Token;
  tokenToCheck: Token;
}): -1 | 0 | 1 => {
  const tokenToCheckSharesIdsWithA = shareSubgraphId(a, tokenToCheck);

  const tokenToCheckSharesIdsWithB = shareSubgraphId(b, tokenToCheck);

  if (tokenToCheckSharesIdsWithA && !tokenToCheckSharesIdsWithB) {
    return -1;
  }

  if (!tokenToCheckSharesIdsWithA && tokenToCheckSharesIdsWithB) {
    return 1;
  }

  return 0;
};

const getParsedDirection = (
  direction: SwapDirection
): "source" | "destination" => {
  return direction === "from" ? "source" : "destination";
};

/**
 * Returns an array of supported chain IDs based on the provided config:
 * - If `availableChains` is defined, only chains in `availableChains` will be returned
 * - If `disabledChains` is defined, chains in `disabledChains` will be removed
 * - If none of the above is defined, returns all chains
 */
export const getSupportedChainIdsForDirection = (
  chainIds: string[],
  config: Pick<AppConfig, "availableChains" | "disabledChains">,
  direction?: SwapDirection
): string[] => {
  if (!direction) return chainIds;

  const parsedDirection = getParsedDirection(direction);
  const availableChains = config.availableChains?.[parsedDirection];

  if (availableChains && availableChains.length > 0) {
    return availableChains;
  }

  const disabledChains = config.disabledChains?.[parsedDirection];

  if (disabledChains && disabledChains.length > 0) {
    return chainIds.filter((c) => !disabledChains.includes(c));
  }

  return chainIds;
};

/**
 * Filter available chains based on provided config and direction
 */
export const filterChains = ({
  chains,
  config,
  direction,
}: {
  chains: ChainData[];
  config: Pick<AppConfig, "disabledChains" | "availableChains">;
  direction?: SwapDirection;
}): ChainData[] => {
  const supportedChainIds = getSupportedChainIdsForDirection(
    chains.map((c) => c.chainId),
    config,
    direction
  );

  const availableChains = chains.filter((c) =>
    supportedChainIds.includes(c.chainId)
  );

  return availableChains;
};

/**
 * Filter tokens based on provided config and direction
 */
export const filterTokens = ({
  tokens,
  config,
  direction,
}: {
  tokens: Token[];
  config: Pick<AppConfig, "disabledChains" | "availableChains">;
  direction?: SwapDirection;
}): Token[] => {
  if (!direction) return tokens;

  const supportedChainIds = getSupportedChainIdsForDirection(
    Array.from(new Set(tokens.map((t) => t.chainId))),
    config,
    direction
  );

  const availableTokens = tokens.filter((t) =>
    supportedChainIds.includes(t.chainId)
  );

  return availableTokens;
};

export function getTokenImage(
  token?: Pick<Token, "address" | "chainId">
): string {
  return `${assetsBaseUrl}/images/migration/webp/${getTokenAssetsKey(token)}.webp`;
}

/**
 * Destination token address has different logic
 * Some tokens are not available based on the source chain/token
 */
const handleDestinationAddressOnSwapChange = ({
  fromChainId,
  fromTokenAddress,
  toTokenAddress,
  toChainId,
  swapRoute,
  chains,
  tokens,
  config,
}: {
  fromChainId: string | undefined;
  fromTokenAddress: string | undefined;
  toTokenAddress: string | undefined;
  toChainId: string | undefined;
  swapRoute: SwapParams | undefined;
  chains: ChainData[];
  tokens: Token[];
  config: Pick<
    AppConfig,
    "initialAssets" | "defaultTokensPerChain" | "disabledChains"
  >;
}) => {
  const srcToken = tokens.find(
    (t) =>
      t.address === (fromTokenAddress ?? swapRoute?.fromTokenAddress) &&
      t.chainId === fromChainId
  );

  const destChain = chains.find(
    (c) => c.chainId === (toChainId ?? swapRoute?.toChainId)
  );

  const removeSourceToken = fromChainId === toChainId;
  const destinationTokensFiltered = filterTokensForDestination({
    tokens: getTokensForChain(tokens, toChainId ?? swapRoute?.toChainId),
    selectedDestinationChain: destChain,
    selectedSourceToken: srcToken,
    removeSourceToken,
  });

  const defaultToAddress = getInitialOrDefaultTokenAddressForChain({
    tokens: destinationTokensFiltered,
    config,
    chainId: toChainId ?? swapRoute?.toChainId,
    direction: "to",
  });

  let destinationTokenAddress =
    toChainId && !toTokenAddress
      ? defaultToAddress
      : (toTokenAddress ?? swapRoute?.toTokenAddress);

  const lowerCaseDestinationTokenAddress =
    destinationTokenAddress?.toLowerCase();

  if (
    destinationTokensFiltered.find(
      (t) => t.address === lowerCaseDestinationTokenAddress
    ) === undefined
  ) {
    destinationTokenAddress = defaultToAddress;
  }
  return lowerCaseDestinationTokenAddress;
};

/**
 * When user changes something from the SwapView
 * param changed can be chains, tokens, or destination address
 *
 * If source chain is changed but source token is undefined, need to find a default token
 * If destination chain is changed but destination token is undefined, need to find a default token
 * If only source token is changed, and destination token is not, and they were the same, then destination token becomes undefined
 * If only destination token is changed but source is not, and they were the same, then source token updates to something else
 *
 */
export const getNewSwapParamsFromInput = ({
  inputParams,
  initialSwapRoute,
  tokens,
  chains,
  config,
  queryClient,
  connectedSourceAddress,
}: {
  inputParams: SwapParams;
  initialSwapRoute?: SwapParams;
  tokens: Token[];
  chains: ChainData[];
  config: Pick<
    AppConfig,
    | "initialAssets"
    | "defaultTokensPerChain"
    | "disabledChains"
    | "availableChains"
  >;
  queryClient: QueryClient;
  connectedSourceAddress?: string;
}): SwapParams => {
  const {
    fromChainId,
    fromTokenAddress,
    toChainId,
    toTokenAddress,
    destinationAddress,
    fallbackAddress,
    depositRefundAddress,
  } = inputParams;

  const srcChainId = fromChainId ?? initialSwapRoute?.fromChainId;
  const destinationChainId = toChainId ?? initialSwapRoute?.toChainId;
  const defaultFromAddress = getInitialOrDefaultTokenAddressForChain({
    tokens,
    config,
    chainId: srcChainId,
    direction: "from",
    excludeToken: toTokenAddress
      ? {
          address: toTokenAddress,
          chainId: destinationChainId ?? "",
        }
      : undefined,
    fetchCachedBalance: {
      address: connectedSourceAddress,
      queryClient,
    },
  });

  // Source token has been changed but not destination
  const onlySourceChanged =
    (!!fromChainId || !!fromTokenAddress) && !toChainId && !toTokenAddress;

  const onlySourceChainIdChanged = onlySourceChanged && !fromTokenAddress;

  // Source is set to the same token as previous destination
  const sameNewSourceAndInitialDest =
    onlySourceChanged &&
    srcChainId === initialSwapRoute?.toChainId &&
    fromTokenAddress === initialSwapRoute?.toTokenAddress;

  // Destination token has been changed but not source
  const onlyDestChanged =
    (!!toChainId || !!toTokenAddress) && !fromChainId && !fromTokenAddress;

  // Destination is set to the same token as previous source
  const sameNewDestAndInitialSource =
    onlyDestChanged &&
    destinationChainId === initialSwapRoute?.fromChainId &&
    toTokenAddress === initialSwapRoute?.fromTokenAddress;

  const sourceTokenAddress =
    sameNewDestAndInitialSource || onlySourceChainIdChanged
      ? defaultFromAddress
      : (fromTokenAddress ?? initialSwapRoute?.fromTokenAddress);

  let destinationTokenAddress = undefined;
  if (!sameNewSourceAndInitialDest) {
    if (onlySourceChainIdChanged) {
      // Preserve the destination token when only the source chain is changed
      destinationTokenAddress =
        toTokenAddress ?? initialSwapRoute?.toTokenAddress;
    } else {
      destinationTokenAddress = handleDestinationAddressOnSwapChange({
        fromChainId,
        fromTokenAddress,
        toTokenAddress,
        toChainId,
        swapRoute: initialSwapRoute,
        chains,
        tokens,
        config,
      });
    }
  }

  let newDestinationAddress: FullAddressData | undefined;
  if (destinationAddress?.address === destinationAddressResetValue) {
    newDestinationAddress = undefined;
  } else {
    const destAddress =
      destinationAddress ?? initialSwapRoute?.destinationAddress;

    const isNewDestAddressValid = isWalletAddressValid(
      chains.find((c) => c.chainId === destinationChainId),
      destAddress?.address
    );

    if (isNewDestAddressValid) {
      newDestinationAddress = destAddress;
    }
  }

  let newFallbackAddress: string | undefined;
  if (
    destinationAddress?.address === destinationAddressResetValue ||
    fallbackAddress === fallbackAddressResetValue
  ) {
    newFallbackAddress = undefined;
  } else {
    newFallbackAddress = fallbackAddress ?? initialSwapRoute?.fallbackAddress;
  }

  const sourceChainChanged =
    fromChainId !== undefined && fromChainId !== initialSwapRoute?.fromChainId;

  const newDepositRefundAddress = sourceChainChanged
    ? undefined
    : depositRefundAddress || initialSwapRoute?.depositRefundAddress;

  return {
    fromChainId: srcChainId,
    fromTokenAddress: sourceTokenAddress,
    toChainId: destinationChainId,
    toTokenAddress: destinationTokenAddress,
    destinationAddress: newDestinationAddress,
    fallbackAddress: newFallbackAddress,
    depositRefundAddress: newDepositRefundAddress,
  };
};

/* Sorts tokens based on the following order:
 * 1. Native token on chain ID "1" (Ethereum) comes first.
 * 2. Other native tokens are sorted by their chain ID priority based on market cap:
 *    - Ethereum (1)
 *    - BNB (56)
 *    - AVAX (43114)
 *    - Polygon (137)
 *    - Filecoin (314)
 *    - Arbitrum (42161)
 *    - Optimism (10)
 *    - Fantom (250)
 * 3. Tokens with symbols "USDC", "USDT", and "axlUSDC" are sorted in that order.
 * 4. If none of the above conditions are met, tokens are sorted by their symbol names.
 */
export const sortAllTokens = (
  tokenA: Pick<Token, "address" | "symbol" | "chainId">,
  tokenB: Pick<Token, "address" | "symbol" | "chainId">
) => {
  const chainIdMcapPriority = [
    "1", // Ethereum
    "56", // BNB
    "43114", // AVAX
    "137", // Polygon
    "314", // Filecoin
    "42161", // Arbitrum
    "10", // Optimism
    "250", // Fantom
  ];

  const getChainIdPriority = (chainId: string) => {
    const index = chainIdMcapPriority.indexOf(chainId);
    return index === -1 ? Number.MAX_SAFE_INTEGER : index;
  };

  const isNative = (token: Pick<Token, "address">) => {
    return token.address.toLowerCase() === nativeEvmTokenAddress;
  };

  if (isNative(tokenA) && isNative(tokenB)) {
    return (
      getChainIdPriority(tokenA.chainId) - getChainIdPriority(tokenB.chainId)
    );
  }

  if (isNative(tokenA)) {
    return -1;
  }

  if (isNative(tokenB)) {
    return 1;
  }

  const tokenPriority = ["USDC", "USDT", "axlUSDC"];
  const indexA = tokenPriority.indexOf(tokenA.symbol);
  const indexB = tokenPriority.indexOf(tokenB.symbol);

  if (indexA !== -1 && indexB !== -1) {
    return indexA - indexB;
  }

  if (indexA !== -1) {
    return -1;
  }

  if (indexB !== -1) {
    return 1;
  }

  return 0;
};

export const findToken = <T extends Pick<Token, "chainId" | "address">>(
  tokens: T[],
  chainId?: string,
  address?: string
) => tokens.find((t) => t.chainId === chainId && t.address === address);

export const findNativeToken = (
  tokens: Token[],
  chain: Pick<ChainData, "chainId"> & {
    nativeCurrency: Pick<ChainData["nativeCurrency"], "symbol">;
  }
): Token | undefined =>
  tokens.find(
    (t) =>
      t.symbol.toUpperCase() === chain.nativeCurrency.symbol.toUpperCase() &&
      t.chainId == chain.chainId
  );

export const normalizeIbcAddress = (address: string): string => {
  if (!address.toLowerCase().startsWith("ibc/")) {
    return address;
  }

  const [prefix, denom] = address.split("/");
  return `${prefix.toLowerCase()}/${denom.toUpperCase()}`;
};

// Native tokens that can have wrapped versions
const nativeWrappedPairs = [
  "ETH",
  "BTC",
  "FIL",
  "BNB",
  "AVAX",
  "POL",
  "FTM",
  "IMX",
  "KAVA",
  "GLMR",
  "CELO",
];

/**
 * Selects the default information for a group of tokens.
 * Prioritizes native tokens (EVM or Cosmos), then falls back to the first unique token symbol.
 */
function selectGroupDefaultInfo(
  tokens: TokenWithBalance[]
): Pick<TokenGroup, "symbol" | "logoURI" | "name"> {
  // Check for native EVM token
  const nativeToken = tokens.find(
    (token) =>
      token.address.toLowerCase() === nativeEvmTokenAddress.toLowerCase()
  );

  if (nativeToken) {
    return {
      symbol: nativeToken.symbol.split(".")[0],
      logoURI: getTokenImage(nativeToken) || "",
      name: nativeToken.name.split(".")[0],
    };
  }

  // Check for native Cosmos token
  // Example ATOM address is "uatom"
  const cosmosNativeToken = tokens.find(
    (token) =>
      !token.address.startsWith("0x") && !token.address.startsWith("ibc/")
  );

  if (cosmosNativeToken) {
    return {
      symbol: cosmosNativeToken.symbol.split(".")[0],
      logoURI: getTokenImage(cosmosNativeToken) || "",
      name: cosmosNativeToken.name.split(".")[0],
    };
  }

  // Sanitize token symbols by removing everything after the first dot
  const sanitizedTokens = tokens.map((token) => ({
    ...token,
    symbol: token.symbol.split(".")[0],
  }));

  // Find the first token with a unique sanitized symbol
  const firstUniqueToken = sanitizedTokens.find(
    (token, index, self) =>
      self.findIndex((t) => t.symbol === token.symbol) === index
  );

  return {
    symbol: firstUniqueToken?.symbol || "",
    logoURI: getTokenImage(firstUniqueToken) || "",
    name: firstUniqueToken?.name.split(".")[0] || "",
  };
}

/**
 * Groups tokens based on their properties (symbol, coingeckoId)
 * Some custom logic to handle native/wrapped pairs
 *
 * @param tokens - Array of TokenWithBalance objects to be grouped
 * @param chainType - Optional parameter to filter tokens by chain type ('evm' or 'cosmos')
 * @returns Object containing grouped tokens and ungrouped tokens
 */
export const groupTokensBySymbol = <T extends TokenWithBalance>(
  tokens: T[],
  chainType?: ChainType
): {
  grouped: TokenGroup[];
  ungrouped: T[];
} => {
  const groups: { [key: string]: TokenGroup } = {};
  // Filter tokens by chain type if specified
  const filteredTokens = chainType
    ? tokens.filter((token) => token.type === chainType)
    : tokens;

  // First pass: group by coingeckoId or symbol
  filteredTokens.forEach((token) => {
    const groupKey = token.coingeckoId || token.symbol.split(".")[0];
    if (!groups[groupKey]) {
      groups[groupKey] = {
        symbol: "",
        logoURI: "",
        name: "",
        tokens: [],
        totalBalance: "0",
        totalBalanceUsd: "0",
        balance: "0",
        chainId: "",
        chainLogoURI: "",
      };
    }
    groups[groupKey].tokens.push(token);
    groups[groupKey].totalBalance = new BigNumber(groups[groupKey].totalBalance)
      .plus(token.balance)
      .toString();

    const tokenUsdValue = convertTokenAmountToUSD(
      token.balance,
      token.usdPrice || 0
    );
    groups[groupKey].totalBalanceUsd = new BigNumber(
      groups[groupKey].totalBalanceUsd ?? "0"
    )
      .plus(tokenUsdValue)
      .toString();
  });

  // Second pass: merge groups based on native/wrapped pairs
  const mergedGroups: { [key: string]: TokenGroup } = {};
  Object.entries(groups).forEach(([key, group]) => {
    const baseSymbol = group.tokens[0].symbol.split(".")[0];
    let mergeKey = key;

    // Handle native/wrapped pairs
    if (
      nativeWrappedPairs.includes(baseSymbol) ||
      (baseSymbol.startsWith("W") &&
        nativeWrappedPairs.includes(baseSymbol.slice(1)))
    ) {
      mergeKey = baseSymbol.replace(/^W/, "");
    }

    // Merge groups or create a new one if it doesn't exist
    if (!mergedGroups[mergeKey]) {
      mergedGroups[mergeKey] = { ...group };
    } else {
      // Combine tokens and sum up total balance for existing groups
      mergedGroups[mergeKey].tokens.push(...group.tokens);
      mergedGroups[mergeKey].totalBalance = new BigNumber(
        mergedGroups[mergeKey].totalBalance
      )
        .plus(group.totalBalance)
        .toString();
      // Add this line to properly merge USD values
      mergedGroups[mergeKey].totalBalanceUsd = new BigNumber(
        mergedGroups[mergeKey].totalBalanceUsd ?? "0"
      )
        .plus(group.totalBalanceUsd ?? "0")
        .toString();
    }
  });

  // Final pass: set group info and separate single-token groups
  const finalGroups: TokenGroup[] = [];
  const ungrouped: T[] = [];

  Object.values(mergedGroups).forEach((group) => {
    if (group.tokens.length === 1) {
      ungrouped.push(group.tokens[0] as T);
    } else {
      const defaultInfo = selectGroupDefaultInfo(group.tokens as T[]);
      finalGroups.push({ ...group, ...defaultInfo });
    }
  });

  return { grouped: finalGroups, ungrouped };
};

/**
 * Groups tokens based on their chainId
 *
 * @param tokens - Array of TokenWithBalance objects to be grouped
 * @param chains - Array of ChainData objects to find chain information
 * @returns Array of TokenGroup objects
 */
export const groupTokensByChainId = <T extends TokenWithBalance>(
  tokens: T[],
  chains: ChainData[]
): TokenGroup[] => {
  const groups: { [key: string]: TokenGroup } = {};

  tokens.forEach((token) => {
    const chainData = chains.find((chain) => chain.chainId === token.chainId);
    const groupKey = token.chainId;
    if (!groups[groupKey]) {
      groups[groupKey] = {
        symbol: chainData?.nativeCurrency.symbol || "",
        logoURI: chainData?.chainIconURI || "",
        name: chainData?.networkName || "",
        tokens: [],
        totalBalance: "0",
        totalBalanceUsd: "0",
        balance: "0",
        chainId: token.chainId,
        chainLogoURI: chainData?.chainIconURI || "",
      };
    }
    groups[groupKey].tokens.push(token);
    groups[groupKey].totalBalance = new BigNumber(groups[groupKey].totalBalance)
      .plus(token.balance)
      .toString();

    // Only add to USD total if token has a valid price
    if (token.usdPrice && token.usdPrice > 0) {
      const tokenUsdValue = convertTokenAmountToUSD(
        token.balance,
        token.usdPrice
      );
      groups[groupKey].totalBalanceUsd = new BigNumber(
        groups[groupKey].totalBalanceUsd ?? "0"
      )
        .plus(tokenUsdValue)
        .toString();
    }
  });

  return Object.values(groups);
};

/**
 * Removes some tokens based on address and chain ID.
 * Some tokens will be returned by backend but should be hidden.
 *
 * Also filters tokens in `config.availableTokens` if provided.
 *
 * TODO: in the future we might want to have this as a token attribute instead.
 */
export const filterViewableTokens = <T extends Token>(
  tokens: T[],
  config: Pick<AppConfig, "availableTokens">,
  direction?: SwapDirection
): T[] => {
  const disabledTokens = [
    // On Celo, native token & custom address tokens are returned
    // so we should hide the native one but keep receiving it to use gas balance
    { chainId: CHAIN_IDS.CELO, address: nativeEvmTokenAddress },
    // SagaEVM is gasless, so the native token is not used
    { chainId: CHAIN_IDS.SAGA_EVM, address: nativeEvmTokenAddress },
    { chainId: CHAIN_IDS.DCHAIN, address: nativeEvmTokenAddress },
  ];

  return (
    tokens.filter((token) => {
      if (direction) {
        const parsedDirection = getParsedDirection(direction);
        const availableTokensForDirection =
          config.availableTokens?.[parsedDirection];
        const tokensForChain = availableTokensForDirection?.[token.chainId];

        // only return tokens that are in the allowed list for this chain and direction
        if (tokensForChain && tokensForChain.length > 0) {
          return tokensForChain.some(
            (address) => address.toLowerCase() === token.address.toLowerCase()
          );
        }
      }

      return !disabledTokens.some(
        (disabledToken) =>
          disabledToken.chainId === token.chainId &&
          disabledToken.address.toLowerCase() === token.address.toLowerCase()
      );
    }) ?? []
  );
};

export const getSecretNetworkBalances = async (
  chainData: ChainData,
  cosmosAddress: string,
  squidTokens: Token[],
  keplrTypeWallet: any
): Promise<TokenWithBalance[]> => {
  const squidSecretTokens = squidTokens.filter(
    (t) => t.chainId === CHAIN_IDS.SECRET
  ) as Token[];

  const { fetchAllSecretBalances } = await import("../external/secretService");

  return fetchAllSecretBalances(
    chainData,
    cosmosAddress,
    squidSecretTokens,
    keplrTypeWallet
  );
};

export function getTokenAssetsKey(token?: Pick<Token, "address" | "chainId">) {
  // Remove slashes and colons and lowercase token address
  return `${token?.chainId}_${token?.address?.replace(/[/\:]/g, "").toLowerCase()}`;
}

export type AssetsColors = {
  chains: Record<
    string,
    {
      bgColor: string;
    }
  >;
  tokens: Record<
    string,
    {
      bgColor: string;
      textColor: string;
    }
  >;
};

export async function fetchAssetsColors(): Promise<AssetsColors> {
  try {
    const response = await fetch(
      `${assetsBaseUrl}/scripts/update-tokens/colors.json`
    );

    const assetsColors: AssetsColors = await response.json();

    const isValidResponse =
      typeof assetsColors === "object" &&
      typeof assetsColors.chains === "object" &&
      typeof assetsColors.tokens === "object";

    if (!isValidResponse) {
      throw new Error("Invalid assets colors response");
    }

    return assetsColors;
  } catch (error) {
    console.error("Error fetching colors file:", error);
    return {
      chains: {},
      tokens: {},
    };
  }
}

const supportedChainTypes = new Set<string>(Object.values(ChainType));

export function isSupportedChainType(
  chainType: string
): chainType is ChainType {
  return supportedChainTypes.has(chainType);
}

/**
 * Normalizes SDK data after each init/refetch before the rest of hooks consumes it.
 *
 * - Removes chains whose chainType is not supported by the local package.
 * - Drops tokens for those removed chains.
 * - Converts Evmos chains/tokens to EVM.
 * - Applies optional asset colors.
 */
export function initializeSquidData(
  squid: Squid,
  assetsColors: AssetsColors = {
    chains: {},
    tokens: {},
  }
): void {
  const supportedChains = squid.chains.filter((chain) =>
    isSupportedChainType(chain.chainType)
  );
  const supportedChainIds = new Set(
    supportedChains.map((chain) => chain.chainId)
  );
  const evmosChainIds = supportedChains
    .filter(isEvmosChain)
    .map((c) => c.chainId);

  const supportedTokens = squid.tokens.filter((token) =>
    supportedChainIds.has(token.chainId)
  );

  squid.tokens = supportedTokens.map((token) => {
    const isEvmosToken = evmosChainIds.includes(token.chainId);

    return {
      ...token,
      type: isEvmosToken ? ChainType.EVM : token.type,
      bgColor: assetsColors.tokens[getTokenAssetsKey(token)]?.bgColor,
      textColor: assetsColors.tokens[getTokenAssetsKey(token)]?.textColor,
    };
  });

  squid.chains = supportedChains.map((chain) => {
    const bgColor = assetsColors.chains[chain.chainId]?.bgColor;

    // convert evmos cosmos chains to evm chains
    // TODO: this will be fixed in the backend
    if (isEvmosChain(chain)) {
      const evmChain = convertEvmosToEvmChain(chain as CosmosChain);

      return {
        ...evmChain,
        bgColor,
      };
    }

    return {
      ...chain,
      bgColor,
    };
  });
}

export function isEmptyObject(
  obj: Record<string, unknown> | undefined
): boolean {
  if (!obj) return true;

  return Object.values(obj).every((value) => value === undefined);
}

export const normalizeTokenSymbol = (symbol: string): string => {
  return symbol.toUpperCase();
};

export const areTokenSymbolsCompatible = (
  symbol1: string,
  symbol2: string
): boolean => {
  return normalizeTokenSymbol(symbol1) === normalizeTokenSymbol(symbol2);
};
export function isEvmosChain(chain: ChainData | undefined): boolean {
  return (chain as CosmosChain)?.isEvmos || false;
}

function convertEvmosToEvmChain(cosmosChain: CosmosChain): EvmChain {
  return {
    // @ts-expect-error - isEvmos is not a property on EvmChain
    // We still need this attribute in evmos chains
    // even after they are converted to evm chains, to perform certain checks
    // e.g skipping erc20 approve/allowance checks
    isEvmos: true,
    chainType: ChainType.EVM,
    axelarChainName: cosmosChain.axelarChainName,
    blockExplorerUrls: cosmosChain.blockExplorerUrls,
    chainId: cosmosChain.chainId,
    bridges: cosmosChain.bridges,
    chainIconURI: cosmosChain.chainIconURI,
    enableBoostByDefault: cosmosChain.enableBoostByDefault,
    chainNativeContracts: {
      ensRegistry: "",
      multicall: "",
      usdcToken: "",
      wrappedNativeToken: "",
    },
    internalRpc: cosmosChain.internalRpc,
    nativeCurrency: {
      decimals: cosmosChain.nativeCurrency.decimals,
      name: cosmosChain.nativeCurrency.name,
      symbol: cosmosChain.nativeCurrency.symbol,
      icon: cosmosChain.nativeCurrency.icon,
    },
    networkName: cosmosChain.networkName,
    rpc: cosmosChain.rpc,
    networkIdentifier: cosmosChain.networkIdentifier,
    sameChainSwapsSupported: cosmosChain.sameChainSwapsSupported,
    squidContracts: cosmosChain.squidContracts,
    swapAmountForGas: cosmosChain.swapAmountForGas,
    boostSupported: cosmosChain.boostSupported,
    compliance: cosmosChain.compliance,
    interchainService: cosmosChain.interchainService,
  };
}
